<template>
  <div>
    <vx-card v-if="searched">
      <div id="data-list-list-view" class="data-list-container">
        <vs-table
          ref="table"
          class="tablePrincipal"
          noDataText="Nenhuma renovação encontrada"
          :data="rows"
        >
          <div
            slot="header"
            class="flex flex-wrap-reverse items-center flex-grow justify-end mt-2 mb-6"
          >
            <PaginationItemsPerPageDropdown
              :items-per-page="itemsPerPage"
              :total-items="total"
              :current-page="currentPage"
              :options="[10, 25, 50, 100]"
              @update:itemsPerPage="changeItemsPerPage"
            />
          </div>

          <template slot="thead">
            <vs-th>Apólice</vs-th>
            <vs-th>Segurado</vs-th>
            <vs-th>Veículo</vs-th>
            <vs-th>Vigência</vs-th>
            <vs-th>Status</vs-th>
            <vs-th>Ações</vs-th>
          </template>

          <template slot-scope="{ data }">
            <tbody>
              <vs-tr
                v-for="(row, index) in data"
                :key="row.policyId"
                :data="row"
                :data-testId="`renewal-row-${index}`"
              >
                <vs-td>
                  <p class="font-medium">{{ row.policyNumber }}</p>
                </vs-td>

                <vs-td>
                  <p>{{ row.insuredName }}</p>
                </vs-td>

                <vs-td>
                  <p class="font-bold" data-testid="vehicle-identifier">{{ vehicleIdentifier(row) }}</p>
                  <p>
                    {{ row.vehicleBrand }} {{ row.vehicleModel }}
                    <span v-if="row.modelYear">- {{ row.modelYear }}</span>
                  </p>
                </vs-td>

                <vs-td>
                  {{ formatDate(row.startValidity) }} à
                  {{ formatDate(row.endValidity) }}
                </vs-td>

                <vs-td>
                  <vs-chip
                    class="rounded-full uppercase"
                    :color="statusColor(row.status)"
                  >
                    {{ row.status }}
                  </vs-chip>
                </vs-td>

                <vs-td>
                  <span
                    class="inline-block"
                    v-tooltip="
                      row.canRenew ? 'Renovar apólice' : 'Renovação indisponível'
                    "
                  >
                    <vs-button
                      radius
                      color="primary"
                      type="gradient"
                      icon-pack="feather"
                      icon="icon-refresh-cw"
                      class="d-inline"
                      :disabled="
                        !row.canRenew || renewingPolicyId === row.policyId
                      "
                      @click="renew(row)"
                      :data-testId="
                        row.canRenew ? 'renew-button' : 'renew-button-disabled'
                      "
                    ></vs-button>
                  </span>
                </vs-td>
              </vs-tr>
            </tbody>
          </template>
        </vs-table>

        <div v-if="totalPages > 1" class="mt-6">
          <vs-pagination
            :total="totalPages"
            :value="currentPage"
            @input="goToPage"
            data-testId="pagination"
          ></vs-pagination>
        </div>
      </div>
    </vx-card>

    <vs-alert
      v-if="!searched"
      color="warning"
      title="Atenção!!"
      active="true"
      class="my-3"
      style="height: auto !important"
      data-testId="renewals-filter-alert"
    >
      É necessário fazer um filtro para exibir as Renovações (clique em buscar)
    </vs-alert>

    <Modal
      title="Cotação Recusada"
      :active.sync="refusalModalActive"
      @close="refusalModalActive = false"
      data-testId="renewal-refused-modal"
    >
      {{ refusalReason }}
      <div class="vx-row">
        <div class="vx-col w-full text-right">
          <vs-button
            @click="refusalModalActive = false"
            type="gradient"
            color="warning"
            class="mt-5"
            data-testId="renewal-refused-close"
          >
            Fechar
          </vs-button>
        </div>
      </div>
    </Modal>
  </div>
</template>
<script>
import moment from 'moment';
import notify from '../../utils/notify';
import renewalsApi from '../../services/renewals.api';
import Modal from '../../components/Modal.vue';
import PaginationItemsPerPageDropdown from '../../components/PaginationItemsPerPageDropdown.vue';
import { RENEWAL_STATUS_COLORS } from '../../constants/renewals';

const ITEMS_PER_PAGE = 50;

export default {
  name: 'ListRenewals',
  components: {
    Modal,
    PaginationItemsPerPageDropdown,
  },
  props: {
    filter: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      rows: [],
      total: 0,
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      fetchToken: 0,
      searched: false,
      renewingPolicyId: null,
      refusalModalActive: false,
      refusalReason: '',
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.itemsPerPage) || 1;
    },
  },
  watch: {
    filter(newFilter) {
      if (!newFilter) {
        this.fetchToken += 1;
        this.rows = [];
        this.total = 0;
        this.searched = false;
        this.currentPage = 1;
        this.$vs.loading.close();
        return;
      }
      this.searched = true;
      this.currentPage = 1;
      this.fetchRenewals();
    },
  },
  methods: {
    goToPage(page) {
      this.currentPage = page;
      this.fetchRenewals();
    },
    changeItemsPerPage(value) {
      this.itemsPerPage = value;
      this.currentPage = 1;
      this.fetchRenewals();
    },
    formatDate(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '';
    },
    vehicleIdentifier(row) {
      return [row.plate, row.chassis].filter(Boolean).join(' - ');
    },
    statusColor(status) {
      return RENEWAL_STATUS_COLORS[status] || 'dark';
    },
    async fetchRenewals() {
      const token = ++this.fetchToken;
      try {
        this.$vs.loading();
        const data = await renewalsApi.getRenewals({
          ...this.filter,
          page: this.currentPage,
          itemsPerPage: this.itemsPerPage,
        });
        if (token !== this.fetchToken) return;
        this.rows = data.rows || [];
        this.total = data.total || 0;
      } catch (error) {
        if (token !== this.fetchToken) return;
        notify.notifyFail(this.$vs, 'Falha ao buscar renovações');
      } finally {
        this.$vs.loading.close();
      }
    },
    async renew(row) {
      try {
        this.renewingPolicyId = row.policyId;
        this.$vs.loading();
        const quotation = await renewalsApi.createRenewalQuotation(
          row.policyId,
        );
        const quotationId = quotation.proposal_id || quotation.id;
        this.$router.push(`/auto/cotacao/${quotationId}`);
      } catch (error) {
        const status = error && error.response && error.response.status;
        const message =
          (error &&
            error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Não foi possível criar a cotação de renovação';

        if (status === 422) {
          this.refusalReason = message;
          this.refusalModalActive = true;
          return;
        }

        notify.notifyFail(this.$vs, message);
      } finally {
        this.renewingPolicyId = null;
        this.$vs.loading.close();
      }
    },
  },
};
</script>
<style lang="scss">
#data-list-list-view {
  .vs-con-table {
    .vs-table--header {
      display: flex;
      flex-wrap: wrap-reverse;
      margin-left: 1.5rem;
      margin-right: 1.5rem;
      > span {
        display: flex;
        flex-grow: 1;
      }
    }

    .vs-table {
      border-collapse: separate;
      border-spacing: 0 1.3rem;
      padding: 0 1rem;

      tr {
        box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
        td {
          padding: 20px;
          &:first-child {
            border-top-left-radius: 0.5rem;
            border-bottom-left-radius: 0.5rem;
          }
          &:last-child {
            border-top-right-radius: 0.5rem;
            border-bottom-right-radius: 0.5rem;
          }
        }
      }
    }

    .vs-table--thead {
      th {
        padding-top: 0;
        padding-bottom: 0;

        .vs-table-text {
          text-transform: uppercase;
          font-weight: 600;
        }
      }
      tr {
        background: none;
        box-shadow: none;
      }
    }

    .vs-table--pagination {
      justify-content: center;
    }
  }
}
</style>
